
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authConfirmEmail } from '@/services/authService';
import logoYonyon from '@/assets/logoYonyon.png';
import { useAuth } from '@/hooks/useAuth';

type Status = 'loading' | 'success' | 'error';

export default function ConfirmEmail() {
    const [searchParams] = useSearchParams();

    const { user, refreshUser } = useAuth();

    const userId = searchParams.get('userId') ?? '';
    const token = searchParams.get('token') ?? '';

    const [status, setStatus] = useState<Status>('loading');
    const [message, setMessage] = useState('Vérification de ton adresse e-mail...');


    useEffect(() => {
        let cancelled = false;

        async function confirmEmail() {
            if (!userId || !token) {
                setStatus('error');
                setMessage(
                    'Ce lien de confirmation est incomplet ou invalide.'
                );
                return;
            }

            try {
                const response = await authConfirmEmail({ userId, token });

                if (cancelled) return;

                setStatus('success');
                setMessage(
                    response.message ||
                    'Ton adresse e-mail a été confirmée avec succès.'
                );

                // Actualise l'utilisateur connecté après la confirmation.

                await refreshUser();

            } catch {
                if (cancelled) return;

                setStatus('error');
                setMessage(
                    'Ce lien est invalide ou a expiré. Tu peux demander un nouvel e-mail de confirmation depuis ton compte.'
                );
            }
        }

        void confirmEmail();

        return () => {
            cancelled = true;
        };
    }, [userId, token, refreshUser]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-background-100 p-4 sm:p-6">
            <div className="w-full max-w-md">
                <Link
                    to="/"
                    className="flex justify-center mb-8"
                    aria-label="Retour à l'accueil YonYon"
                >
                    <img
                        src={logoYonyon}
                        alt="YonYon"
                        className="h-24 w-auto object-contain"
                    />
                </Link>

                <div className="bg-white rounded-2xl shadow-sm border border-background-100/50 p-6 sm:p-8 text-center">
                    <div
                        className={`mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full ${status === 'success'
                            ? 'bg-green-50 text-green-600'
                            : status === 'error'
                                ? 'bg-primary-50 text-primary-600'
                                : 'bg-background-100 text-foreground-500'
                            }`}
                        aria-hidden="true"
                    >
                        <i
                            className={`text-2xl ${status === 'success'
                                ? 'ri-mail-check-line'
                                : status === 'error'
                                    ? 'ri-mail-close-line'
                                    : 'ri-loader-4-line animate-spin'
                                }`}
                        />
                    </div>

                    <h1 className="font-heading text-2xl font-semibold text-foreground-950">
                        {status === 'success'
                            ? 'Adresse e-mail confirmée !'
                            : status === 'error'
                                ? 'Confirmation impossible'
                                : 'Confirmation en cours'}
                    </h1>

                    <p
                        className="mt-3 text-sm leading-relaxed text-foreground-500"
                        role="status"
                        aria-live="polite"
                    >
                        {message}
                    </p>

                    <Link
                        to={status === 'success' ? (user ? '/mon-profil' : '/auth') : '/'}
                        className="mt-6 w-full px-4 py-3 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors inline-flex items-center justify-center gap-2"
                    >
                        {status === 'success'
                            ? user
                                ? 'Retour à mon profil'
                                : 'Continuer vers la connexion'
                            : 'Retour à YonYon'}
                        <i className="ri-arrow-right-line" aria-hidden="true" />
                    </Link>
                </div>

                <Link
                    to="/"
                    className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-foreground-500 hover:text-foreground-700 transition-colors"
                >
                    <i className="ri-arrow-left-line" aria-hidden="true" />
                    Retour à l'accueil
                </Link>
            </div>
        </div>
    );
}